import api from './index';
import { getUserId } from './auth';

export const trademarkService = {
  // Update a trademark with application ID to ensure bidirectional linking
  updateTrademarkApplication: async (trademarkId, applicationId) => {
    try {
      if (!trademarkId || !applicationId) {
        throw new Error('Trademark ID and Application ID are required');
      }
      
      console.log('Updating trademark', trademarkId, 'with applicationId:', applicationId);
      
      // Use POST with id in body, following trademark API pattern
      const response = await api.post(`/trademarks`, {
        id: trademarkId,
        applicationId: applicationId
      });
      
      console.log('Trademark updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Error updating trademark ${trademarkId} with application ${applicationId}:`, error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      throw error;
    }
  },
  // Get all trademarks for the current user
  getTrademarks: async (userId = null) => {
    try {
      // Get user ID from auth utility if not provided
      const identifier = userId || getUserId();
      
      if (!identifier) {
        throw new Error('User not authenticated');
      }
      
      const response = await api.get('/trademarks', { params: { userId: identifier } });
      return response.data;
    } catch (error) {
      console.error('Error fetching trademarks:', error);
      throw error;
    }
  },
  
  // Get trademarks for a specific application
  getTrademarksByApplication: async (applicationId) => {
    try {
      if (!applicationId) {
        throw new Error('Application ID is required');
      }
      
      console.log('🔍 trademarkService.getTrademarksByApplication called with:', applicationId); // This will show the full call stack
      console.log('🔍 Making API call: GET /trademarks with params:', { applicationId });
      
      const response = await api.get('/trademarks', { params: { applicationId } });
      
      console.log('🔍 trademarkService API response:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching trademarks for application ${applicationId}:`, error);
      throw error;
    }
  },
  
  // Process trademark with Vision API
  processWithVisionAPI: async (trademarkId) => {
    try {
      if (!trademarkId) {
        throw new Error('Trademark ID is required');
      }
      
      const response = await api.post('/visionapi', { trademarkId });
      return response.data;
    } catch (error) {
      console.error(`Error processing trademark ${trademarkId} with Vision API:`, error);
      throw error;
    }
  },

  // Get vision analysis results for a trademark
  getVisionAnalysis: async (trademarkId) => {
    try {
      if (!trademarkId) {
        throw new Error('Trademark ID is required');
      }
      
      const response = await api.get(`/visionAnalysis?trademarkId=${trademarkId}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting vision analysis for trademark ${trademarkId}:`, error);
      throw error;
    }
  },
  
  // Get a specific trademark by ID
  getTrademark: async (id) => {
    try {
      const response = await api.get(`/trademarks`, {
        params: { id }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching trademark ${id}:`, error);
      throw error;
    }
  },
  
  // Create a new trademark
  createTrademark: async (data) => {
    try {
      // Make sure we have the applicationId from sessionStorage if not provided
      const applicationId = data.applicationId || sessionStorage.getItem('currentApplicationId');
      const payload = {
        ...data,
        applicationId
      };
      
      const response = await api.post('/trademarks', payload);
      return response.data;
    } catch (error) {
      console.error('Error creating trademark:', error);
      throw error;
    }
  },
  
  // Update an existing trademark
  updateTrademark: async (id, data) => {
    try {
      const response = await api.post('/trademarks', {
        id,
        ...data
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating trademark ${id}:`, error);
      throw error;
    }
  },
  
  // Delete a trademark
  deleteTrademark: async (id) => {
    try {
      const response = await api.post('/Database/trademarks/delete', { id });
      return response.data;
    } catch (error) {
      console.error(`Error deleting trademark ${id}:`, error);
      throw error;
    }
  },
  
  // Upload a trademark image
  uploadTrademarkImage: async (id, file) => {
    try {
      // Convert file to base64 for JSON transmission
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = async () => {
          try {
            // Get the base64 data
            const base64data = reader.result;
            
            // Create payload for the trademarks API
            const payload = {
              id: id,
              type: 'figurative',
              content: {
                data: base64data,
                name: file.name
              }
            };
            
            // Get user ID - authentication is required
            const userId = getUserId();
            
            if (!userId) {
              throw new Error('User not authenticated');
            }
            
            // Add authentication info
            payload.userId = userId;
            
            // Use the main trademarks API
            const response = await api.post('/trademarks', payload, {
              headers: {
                'Content-Type': 'application/json'
              },
              timeout: 30000 // 30 second timeout for large image uploads
            });
            
            resolve(response.data);
          } catch (error) {
            console.error(`Error uploading image for trademark ${id}:`, error);
            reject(error);
          }
        };
        reader.onerror = (error) => {
          console.error('Error reading file:', error);
          reject(error);
        };
      });
    } catch (error) {
      console.error(`Error uploading image for trademark ${id}:`, error);
      throw error;
    }
  },
  
  // Upload a trademark (word mark or logo)
  uploadTrademark: async (trademarkData) => {
    try {
      console.log('Uploading trademark with data:', trademarkData);
      
      // Get user ID from input or auth utility
      let userId = trademarkData.userId || getUserId();
      const threadId = trademarkData.threadId; // Get threadId if provided
      const applicationId = trademarkData.applicationId || sessionStorage.getItem('currentApplicationId'); // Get applicationId
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      console.log('📝 uploadTrademark: IDs being used:', {
        userId,
        threadId,
        applicationId,
        trademarkType: trademarkData.type
      });
      
      // Prepare data for upload based on trademark type
      let dataToSend;
      
      if (trademarkData.type === 'word') {
        // For word trademarks, use simple data structure
        dataToSend = {
          id: trademarkData.id || undefined,
          userId,
          threadId, // Include OpenAI threadId
          applicationId, // 🔗 CRITICAL: Include applicationId for bidirectional linking
          type: 'word',
          content: trademarkData.content,
          name: trademarkData.content, // Set name for the API validation
          timestamp: trademarkData.timestamp || new Date().toISOString(),
          status: trademarkData.status || 'pending'
        };
      } else {
        // For image trademarks, ensure proper content structure
        dataToSend = {
          id: trademarkData.id || undefined,
          userId,
          threadId, // Include OpenAI threadId
          applicationId, // 🔗 CRITICAL: Include applicationId for bidirectional linking
          type: trademarkData.type,
          content: {
            name: trademarkData.content?.name || 'image.png',
            type: trademarkData.content?.type || 'image/png',
            size: trademarkData.content?.size,
            data: trademarkData.content?.data
          },
          name: trademarkData.content?.name || 'Trademark Image', // Set name for the API validation
          timestamp: trademarkData.timestamp || new Date().toISOString(),
          status: trademarkData.status || 'pending'
        };
      }
      
      // Use the main trademarks endpoint directly for all uploads
      const response = await api.post('/trademarks', dataToSend, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        timeout: 30000 // 30 second timeout for large image uploads
      });
      
      const savedTrademark = response.data;
      console.log('Trademark upload successful:', savedTrademark.id);
      
      // Automatically trigger Vision API processing in the background if it's a logo or figurative trademark
      if ((dataToSend.type === 'logo' || dataToSend.type === 'figurative' || dataToSend.type === 'word') && savedTrademark.id) {
        console.log('Queuing Vision API processing for trademark:', savedTrademark.id, 'type:', dataToSend.type);
        
        // Fire and forget - don't await the response but implement retry logic
        setTimeout(async () => {
          // Prepare the payload with all necessary context
          const payload = {
            trademark: savedTrademark,
            threadId: threadId || savedTrademark.threadId,
            applicationId: dataToSend.applicationId || savedTrademark.applicationId,
            userId: savedTrademark.userId || dataToSend.userId
          };
          
          // Logging the full context being sent for vision processing
          console.log('Vision API context being sent:', {
            trademarkId: savedTrademark.id,
            threadId: payload.threadId,
            applicationId: payload.applicationId,
            userId: payload.userId
          });
          
          // Implement retry logic for better resilience
          let retries = 0;
          const maxRetries = 3;
          let success = false;
          
          while (retries < maxRetries && !success) {
            try {
              const response = await api.post('/visionapi', payload, {
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                },
                timeout: 120000 // 2 minute timeout for vision processing
              });
              
              console.log('Background Vision API processing successful:', response.data);
              console.log('🔄 Vision API completed during upload, notifying TrademarkContext to reload');
              const event = new CustomEvent('trademark_vision_completed', {
                detail: { trademarkId: savedTrademark.id }
              });
              window.dispatchEvent(event);
              success = true;
            } catch (visionError) {
              retries++;
              console.error(`Vision API processing attempt ${retries} failed:`, visionError.message);
              
              if (retries < maxRetries) {
                // Wait before retry with exponential backoff
                await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retries)));
                console.log(`Retrying Vision API processing (attempt ${retries + 1} of ${maxRetries})`);
              } else {
                console.error('Vision API processing failed after maximum retries');
              }
            }
          }
        }, 100); // Small delay to ensure upload completes first
      }
      
      return savedTrademark;
    } catch (error) {
      console.error('Error uploading trademark:', error);
      throw error;
    }
  },
  
  // Update a trademark with user ID (used for transferring from session to user)
  updateTrademarkUser: async (id, userId) => {
    try {
      console.log(`Updating trademark ${id} with user ID ${userId}`);
      const response = await api.post('/Database/trademarks/update', {
        id,
        userId,
        sessionId: null // Clear session ID when assigning to a user
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating trademark user ${id}:`, error);
      throw error;
    }
  }
};

export default trademarkService;
